import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { money, n, fmtDuration, fmtTime, computeBudget, buildBudgetText, normalizeWhatsPhone } from "../calc";
import { generateQuotePDF, generateFinancePDF } from "../pdf";
import RouteMap, { buildNavUrl, FleetMap } from "../components/RouteMap";
import AssinaturaTab from "./AssinaturaTab";
import EventosTab from "./EventosTab";
import AjudaTab from "./AjudaTab";
import ServicosTab from "./ServicosTab";
import { PageGuide } from "../components/PageGuide";

const pad = (x) => String(x).padStart(2, "0");
const nowLocal = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };

const QUOTE_STATUS = [
  { v: "orcamento", l: "Orçamento" }, { v: "aprovado", l: "Aprovado" }, { v: "agendado", l: "Agendado" },
  { v: "em_andamento", l: "Em andamento" }, { v: "realizado", l: "Realizado" },
  { v: "recusado", l: "Recusado" }, { v: "cancelado", l: "Cancelado" },
];
const TRANSPORT_STATUS = [
  { v: "agendada", l: "Agendada" }, { v: "em_andamento", l: "Em andamento" },
  { v: "realizada", l: "Realizada" }, { v: "cancelada", l: "Cancelada" },
];
const qLabel = (v) => QUOTE_STATUS.find((s) => s.v === v)?.l || v;
const tLabel = (v) => TRANSPORT_STATUS.find((s) => s.v === v)?.l || v;

const defaultForm = () => ({
  cepOrigem: "", origem: "", cepDestino: "", destino: "", cepClinica: "", clinicaNome: "", clinicaEndereco: "",
  saida: nowLocal(), permanencia: "30", permanenciaClinica: "30", idaVolta: true,
  kmIda: "0", tempoIda: "0", pedagio: "0", kmPacienteClinica: "0", tempoPacienteClinica: "0", pedagioPacienteClinica: "0",
  kmRetorno: "0", tempoRetorno: "0", pedagioRetorno: "0", veiculo: "", consumo: "10", precoComb: "6.20",
  custoKm: "0", alimentacao: "0", hospedagem: "0", estacionamento: "0", outras: "0",
  modeloMargem: "margem", margem: "30", valorMinimo: "0", adicional: "0",
  familia: "", telefoneWhats: "", dataRemocao: today(), orcOrigem: "", tipoVeiculo: "carro",
  serviceType: "remocao", routeMode: "3pontos",
});

export const SERVICE_TYPES = [
  { v: "remocao", l: "Remoção" },
  { v: "traslado_hospitalar", l: "Traslado hospitalar" },
  { v: "consulta_dentista", l: "Consulta / Dentista" },
  { v: "aeroporto", l: "Aeroporto" },
  { v: "rodoviaria", l: "Rodoviária" },
  { v: "alta_hospitalar", l: "Alta hospitalar" },
  { v: "busca_residencia", l: "Busca em residência" },
  { v: "outro", l: "Outro" },
];
export const serviceLabel = (v) => (SERVICE_TYPES.find((s) => s.v === v)?.l || "Remoção");

const AXLES_MAP = { carro: 2, van: 2, caminhao3: 3, caminhao4: 4, caminhao5: 5 };

const NAV = [
  { id: "dashboard", label: "Painel", icon: "▦", roles: ["admin", "operacional", "financeiro"] },
  { id: "pacientes", label: "Pacientes", icon: "♟", roles: ["admin", "operacional"] },
  { id: "viagem", label: "Novo Orçamento", icon: "＋", roles: ["admin", "operacional"] },
  { id: "orcamentos", label: "Meus Orçamentos", icon: "▤", roles: ["admin", "operacional", "financeiro"] },
  { id: "rotas", label: "Rotas e Viagens", icon: "⌖", roles: ["admin", "operacional", "financeiro"] },
  { id: "rastreamento", label: "Rastreamento", icon: "◎", roles: ["admin", "operacional", "financeiro"] },
  { id: "profissionais", label: "Profissionais", icon: "♟", roles: ["admin", "operacional"] },
  { id: "veiculos", label: "Frota", icon: "▣", roles: ["admin", "operacional"] },
  { id: "financeiro", label: "Financeiro", icon: "R$", roles: ["admin", "financeiro"] },
  { id: "eventos", label: "Eventos", icon: "❖", roles: ["admin"] },
  { id: "servicos", label: "Serviços", icon: "✚", roles: ["admin", "financeiro"] },
  { id: "assinatura", label: "Assinatura", icon: "★", roles: ["admin"] },
  { id: "usuarios", label: "Usuários", icon: "◉", roles: ["admin"] },
  { id: "empresa", label: "Minha Empresa", icon: "◆", roles: ["admin"] },
  { id: "ajuda", label: "Ajuda", icon: "?", roles: ["admin", "operacional", "financeiro"] },
];

function Modal({ title, onClose, children, footer, size }) {
  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal" style={size ? { maxWidth: size } : {}} onClick={(e) => e.stopPropagation()}>
        <div className="rs-modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close">×</button></div>
        <div className="rs-modal-body">{children}</div>
        {footer && <div className="rs-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const role = user?.role;
  const can = (roles) => roles.includes(role);
  const tabs = NAV.filter((t) => can(t.roles));

  const [tab, setTab] = useState(tabs[0]?.id || "dashboard");
  const [blocked, setBlocked] = useState(null);
  const [userMenu, setUserMenu] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [dismissUpgrade, setDismissUpgrade] = useState(false);

  const [patients, setPatients] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [transports, setTransports] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [settings, setSettings] = useState({});
  const [cepCache, setCepCache] = useState({});
  const [users, setUsers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [finance, setFinance] = useState(null);
  const [financeMonth, setFinanceMonth] = useState(thisMonth());
  const [subInfo, setSubInfo] = useState(null);

  // budget state
  const [f, setF] = useState(defaultForm());
  const [teamSel, setTeamSel] = useState({});
  const [manualRate, setManualRate] = useState(false);
  const [budgetStep, setBudgetStep] = useState("viagem");
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [currentPatientId, setCurrentPatientId] = useState(null);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handle402 = (e) => {
    const d = e.response?.data?.detail;
    if (e.response?.status === 402 && d && typeof d === "object") { setBlocked(d); return true; }
    return false;
  };

  const loadAll = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get("/company/settings"), api.get("/dashboard"),
        api.get("/patients"), api.get("/professionals"), api.get("/vehicles"),
        api.get("/maintenance"), api.get("/quotes"), api.get("/transports"),
        api.get("/subscription"),
      ]);
      const [rs, rd, rp, rpro, rv, rm, rq, rt, rsub] = results;
      for (const r of results) if (r.status === "rejected" && handle402(r.reason)) return;
      if (rs.status === "fulfilled") setSettings(rs.value.data);
      if (rd.status === "fulfilled") setDashboard(rd.value.data);
      if (rp.status === "fulfilled") setPatients(rp.value.data);
      if (rpro.status === "fulfilled") setProfessionals(rpro.value.data);
      if (rv.status === "fulfilled") setVehicles(rv.value.data);
      if (rm.status === "fulfilled") setMaintenance(rm.value.data);
      if (rq.status === "fulfilled") setQuotes(rq.value.data);
      if (rt.status === "fulfilled") setTransports(rt.value.data);
      if (rsub.status === "fulfilled") setSubInfo(rsub.value.data);
    } catch (e) { if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [role]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (tab === "financeiro" && can(["admin", "financeiro"])) {
      api.get(`/finance?month=${financeMonth}`).then((r) => setFinance(r.data)).catch((e) => { if (!handle402(e)) toast.error("Falha ao carregar financeiro"); });
    }
    if (tab === "usuarios" && role === "admin") api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [tab, financeMonth]);

  const activeProfessionals = professionals.filter((p) => p.active !== false);
  const calc = useMemo(() => computeBudget(f, teamSel, activeProfessionals, manualRate), [f, teamSel, activeProfessionals, manualRate]);
  const selectedVehicle = vehicles.find((v) => String(v.id) === String(f.veiculo));
  const currentPatient = currentPatientId ? patients.find((p) => String(p.id) === String(currentPatientId)) : null;
  const budgetText = useMemo(() => buildBudgetText(f, calc, currentPatient, selectedVehicle?.name, settings), [f, calc, currentPatient, selectedVehicle, settings]);

  // -------- CEP --------
  const composeAddr = (d, numero) => [[d.logradouro, (numero || "").trim()].filter(Boolean).join(", "), d.bairro, d.localidade, d.uf, d.cep].filter(Boolean).join(", ");
  const buscaCep = async (cepField, targetField, numeroField) => {
    const c = (f[cepField] || "").replace(/\D/g, "");
    if (c.length !== 8) return toast.error("Informe um CEP com 8 números.");
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`); const d = await r.json();
      if (d.erro) throw new Error();
      setCepCache((p) => ({ ...p, [targetField]: d }));
      upd(targetField, composeAddr(d, f[numeroField]));
    } catch { toast.error("Não foi possível consultar o CEP. Preencha manualmente."); }
  };
  const setNumero = (numeroField, targetField) => (e) => {
    const val = e.target.value;
    setF((p) => {
      const next = { ...p, [numeroField]: val };
      const d = cepCache[targetField];
      if (d) next[targetField] = composeAddr(d, val);
      return next;
    });
  };
  const prefillCepCache = async (form) => {
    const jobs = [["cepOrigem", "origem"], ["cepDestino", "destino"], ["cepClinica", "clinicaEndereco"]];
    const updates = {};
    await Promise.all(jobs.map(async ([cepField, target]) => {
      const c = (form[cepField] || "").replace(/\D/g, "");
      if (c.length !== 8) return;
      try { const r = await fetch(`https://viacep.com.br/ws/${c}/json/`); const d = await r.json(); if (!d.erro) updates[target] = d; } catch { /* ignore */ }
    }));
    if (Object.keys(updates).length) setCepCache((p) => ({ ...p, ...updates }));
  };

  const onVehicleChange = (id) => {
    const v = vehicles.find((x) => String(x.id) === String(id));
    setF((p) => ({ ...p, veiculo: id, consumo: v ? String(v.consumption) : "10", precoComb: v ? String(v.fuel) : "6.20" }));
  };

  const toggleTeam = (id, checked) => setTeamSel((p) => ({ ...p, [id]: { checked, qty: p[id]?.qty || 1 } }));
  const setTeamQty = (id, qty) => setTeamSel((p) => ({ ...p, [id]: { checked: p[id]?.checked || false, qty } }));

  // -------- budget flow --------
  const startNew = () => {
    setF(defaultForm()); setTeamSel({}); setManualRate(false); setCurrentQuoteId(null); setCurrentPatientId(null); setRouteData(null); setCepCache({}); setBudgetStep("viagem"); setTab("viagem");
  };

  const goToCosts = () => {
    const miss = [];
    if (!f.origem.trim()) miss.push("origem da remoção");
    if (!f.destino.trim()) miss.push("local do paciente");
    if (f.routeMode !== "ponto_a_ponto" && !f.clinicaNome.trim()) miss.push("nome da clínica");
    if (f.routeMode !== "ponto_a_ponto" && !f.clinicaEndereco.trim()) miss.push("endereço da clínica");
    if (!f.veiculo) miss.push("veículo da frota");
    if (n(f.kmIda) <= 0) miss.push("km Origem → Paciente");
    if (f.routeMode !== "ponto_a_ponto" && n(f.kmPacienteClinica) <= 0) miss.push("km Paciente → Clínica");
    if (f.idaVolta && n(f.kmRetorno) <= 0) miss.push(f.routeMode === "ponto_a_ponto" ? "km Destino → Origem" : "km Clínica → Origem");
    if (miss.length) return toast.error("Preencha os campos obrigatórios antes de continuar: " + miss.join(", ") + ".");
    if (!f.orcOrigem) upd("orcOrigem", f.origem);
    setBudgetStep("remocao");
  };

  const quotePayload = (status) => ({
    patientId: currentPatientId, patientData: currentPatient || null,
    family: f.familia || "Não informado", phone: f.telefoneWhats || "", date: f.dataRemocao || "",
    origin: f.orcOrigem || f.origem || "A definir", destination: f.destino, patientDestination: f.destino,
    clinicName: f.routeMode === "ponto_a_ponto" ? "" : f.clinicaNome, clinicAddress: f.routeMode === "ponto_a_ponto" ? "" : f.clinicaEndereco, clinicCep: f.cepClinica, km: calc.km, value: calc.recomendado,
    serviceType: f.serviceType, routeMode: f.routeMode,
    cost: calc.costBase, profit: calc.lucro, team: calc.teamNames,
    teamDetail: activeProfessionals.filter((p) => teamSel[p.id]?.checked).map((p) => ({ name: p.name, qty: Math.max(1, n(teamSel[p.id].qty) || 1) })),
    teamCost: calc.teamCost, fuelCost: calc.cComb, tollCost: calc.cPed, miscCost: calc.misc, pricePerKm: calc.valorKm,
    vehicleId: f.veiculo || "", vehicleName: selectedVehicle?.name || "", text: budgetText,
    form: { ...f, manualRatePerKm: manualRate }, ...(status ? { status } : {}),
  });

  const saveQuote = async () => {
    try {
      if (currentQuoteId) { await api.patch(`/quotes/${currentQuoteId}`, quotePayload()); toast.success("Orçamento atualizado."); }
      else { const { data } = await api.post("/quotes", quotePayload("orcamento")); setCurrentQuoteId(data.id); toast.success("Orçamento salvo."); }
      loadAll();
    } catch (e) { if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editQuote = (q) => {
    const legacy = q.form ? {} : { origem: q.origin && q.origin !== "A definir" ? q.origin : "", orcOrigem: q.origin || "", destino: q.patientDestination || q.destination || "", clinicaNome: q.clinicName || "", clinicaEndereco: q.clinicAddress || "", familia: q.family || "", telefoneWhats: q.phone || "", dataRemocao: q.date || "", serviceType: q.serviceType || "remocao", routeMode: q.routeMode || "3pontos", veiculo: q.vehicleId || "", kmIda: q.km ? String(q.km) : "" };
    const form = { ...defaultForm(), ...legacy, ...(q.form || {}) };
    setF(form); setManualRate(!!q.form?.manualRatePerKm); setCurrentQuoteId(q.id); setCurrentPatientId(q.patientId || null); setRouteData(form.routePoints ? { points: form.routePoints, polyline: form.routePolyline } : null); setCepCache({}); prefillCepCache(form);
    const sel = {}; (q.teamDetail || []).forEach((d) => { const pro = professionals.find((p) => p.name === d.name); if (pro) sel[pro.id] = { checked: true, qty: d.qty || 1 }; });
    setTeamSel(sel); setBudgetStep("viagem"); setTab("viagem");
  };

  const changeQuoteStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}/status`, { status }); loadAll(); } catch (e) { if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteQuote = async (id) => { if (!window.confirm("Excluir este orçamento?")) return; try { await api.delete(`/quotes/${id}`); loadAll(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const convertQuote = async (id) => {
    try { await api.post(`/quotes/${id}/convert`); toast.success("Orçamento convertido em remoção (agendada)."); loadAll(); setTab("rotas"); }
    catch (e) { if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeTransportStatus = async (id, status) => {
    try { await api.patch(`/transports/${id}/status`, { status }); toast.success("Remoção atualizada."); loadAll(); }
    catch (e) { if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteTransport = async (id) => {
    if (!window.confirm("Excluir esta remoção? Ela sairá de Rotas, Rastreamento e Financeiro.")) return;
    try { await api.delete(`/transports/${id}`); toast.success("Remoção excluída."); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [routeLoading, setRouteLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const calcRouteAuto = async () => {
    const p2p = f.routeMode === "ponto_a_ponto";
    if (!f.origem || !f.destino || (!p2p && !f.clinicaEndereco)) return toast.error(p2p ? "Preencha origem e destino." : "Preencha origem, endereço do paciente e endereço da clínica.");
    setRouteLoading(true);
    try {
      const { data } = await api.post("/routes/calculate", { origem: f.origem, paciente: f.destino, clinica: p2p ? f.destino : f.clinicaEndereco, roundTrip: f.idaVolta, axles: AXLES_MAP[f.tipoVeiculo] || 2 });
      const L = data.legs;
      if (p2p) L.patientClinic = { km: 0, mins: 0, toll: 0 };
      setF((p) => ({ ...p,
        kmIda: String(L.originPatient.km), tempoIda: String(L.originPatient.mins), pedagio: String(L.originPatient.toll),
        kmPacienteClinica: String(L.patientClinic.km), tempoPacienteClinica: String(L.patientClinic.mins), pedagioPacienteClinica: String(L.patientClinic.toll),
        kmRetorno: String(L.clinicOrigin.km), tempoRetorno: String(L.clinicOrigin.mins), pedagioRetorno: String(L.clinicOrigin.toll) }));
      toast.success(data.cached ? "Rota carregada do cache (sem nova consulta ao Google)." : "Rota calculada pelo Google Maps (km, tempo e pedágio).");
      if (data.points) { setRouteData({ points: data.points, polyline: data.polyline }); setF((p) => ({ ...p, routePoints: data.points, routePolyline: data.polyline })); }
    } catch (e) {
      if (e.response?.status === 429) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Limite diário de cálculos automáticos atingido. Preencha os trechos manualmente ou tente amanhã.");
      else if (!handle402(e)) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRouteLoading(false); }
  };

  const sendWhats = () => {
    const phone = normalizeWhatsPhone(f.telefoneWhats);
    if (phone.length !== 12 && phone.length !== 13) return toast.error("Informe um telefone/WhatsApp válido com DDD.");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(budgetText.trim())}`, "_blank", "noopener,noreferrer");
  };

  // -------- generic CRUD helpers --------
  const selectPatient = (p) => { setCurrentPatientId(p.id); upd("familia", p.family || ""); upd("telefoneWhats", p.phone || ""); setTab("viagem"); setBudgetStep("viagem"); };

  if (blocked) {
    return (
      <div className="blocked-wrap">
        <div className="blocked-card">
          <img src="/rotasaude-logo.png" alt="RotaSaúde" />
          <h2>{blocked.code === "trial_expired" ? "Seu período de teste terminou" : "Acesso suspenso"}</h2>
          <p>{blocked.message || "Assine o RotaSaúde para continuar utilizando seus dados e sua operação."} Seus dados estão preservados.</p>
          <button className="primary" style={{ border: 0, borderRadius: 10, padding: "12px 22px", fontWeight: 800, cursor: "pointer" }} onClick={logout} data-testid="blocked-logout">Sair</button>
        </div>
      </div>
    );
  }

  const company = user?.company;
  const trialDaysLeft = company?.status === "trial" && company?.trialEndsAt
    ? Math.ceil((new Date(company.trialEndsAt) - new Date()) / 86400000) : null;

  return (
    <div className="app-shell">
      {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} data-testid="nav-overlay" />}
      {/* Sidebar */}
      <aside className={`sidebar ${navOpen ? "open" : ""}`}>
        <button className="sidebar-close" onClick={() => setNavOpen(false)} data-testid="nav-close" aria-label="Fechar menu">×</button>
        <div className="brand-mini"><img src="/rotasaude-logo.png" alt="RotaSaúde" /></div>
        <nav className="side-nav">
          {tabs.map((t) => (
            <button key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} data-tab={t.id} data-testid={`nav-${t.id}`}
              onClick={() => { if (t.id === "viagem") startNew(); else setTab(t.id); setNavOpen(false); }}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </nav>
        <div className="side-message"><strong>Planejamento</strong><span>Segurança e cuidado</span><span>em cada destino.</span></div>
      </aside>

      <div className="main-area">
        {/* Topbar */}
        <header className="topbar">
          <button className="topbar-hamburger" onClick={() => setNavOpen(true)} data-testid="mobile-menu-toggle" aria-label="Abrir menu">☰</button>
          <div className="top-brand-identity">
            {settings.logo ? <img className="partner-logo-top" src={settings.logo} alt="Empresa parceira" /> : null}
            <div className="top-brand-text"><strong>RotaSaúde</strong><small>{settings.name || company?.nomeFantasia || "Gestão de transporte e remoção de pacientes"}</small></div>
          </div>
          <div className="top-actions">
            <div className="tagline">Planejar hoje. Salvar mais vidas amanhã.</div>
            <div className="user-menu">
              <span className="avatar" onClick={() => setUserMenu((v) => !v)} data-testid="user-menu-toggle">{(user?.name || "U").slice(0, 2).toUpperCase()}</span>
              {userMenu && (
                <div className="user-dropdown">
                  <div className="u-head"><strong>{user?.name}</strong><small>{user?.email}</small><span className="role-badge">{role}</span></div>
                  <button onClick={() => { setShowPw(true); setUserMenu(false); }} data-testid="menu-change-pw">Alterar senha</button>
                  <button onClick={logout} data-testid="menu-logout">Sair</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main>
          {trialDaysLeft !== null && (
            <div className={`trial-banner ${trialDaysLeft <= 3 ? "danger" : ""}`} data-testid="trial-banner">
              Período de teste: {trialDaysLeft > 0 ? `${trialDaysLeft} dia(s) restante(s)` : "expirado"}. Assine o RotaSaúde para manter sua operação ativa.
            </div>
          )}
          {company?.status === "past_due" && (
            <div className="trial-banner danger" data-testid="pastdue-banner">
              ⚠️ Pagamento pendente da sua assinatura. Regularize para evitar a suspensão do acesso após o período de tolerância.
              {role === "admin" && <button className="tb-btn" onClick={() => setTab("assinatura")} data-testid="pastdue-cta">Regularizar</button>}
            </div>
          )}
          {(() => {
            const mr = subInfo?.plan?.maxRemovals, ur = subInfo?.usage?.removals;
            const near = mr && ur != null && ur >= Math.ceil(mr * 0.8);
            if (!near || dismissUpgrade) return null;
            const reached = ur >= mr;
            return (
              <div className="trial-banner danger" data-testid="removal-upgrade-banner">
                {reached ? `⚠️ Você atingiu o limite de ${mr} remoções deste mês.` : `⚠️ Remoções do mês: ${ur}/${mr} — você está perto do limite do seu plano.`} Faça upgrade para não travar novas remoções.
                {role === "admin" && <button className="tb-btn" onClick={() => setTab("assinatura")} data-testid="removal-upgrade-cta">Fazer upgrade</button>}
                <button className="tb-btn" style={{ marginLeft: 8, background: "transparent", border: "1px solid rgba(255,255,255,.55)" }} onClick={() => setDismissUpgrade(true)} data-testid="removal-upgrade-dismiss">Dispensar</button>
              </div>
            );
          })()}

          <PageGuide key={tab} tab={tab} />

          {/* Stats row (visible on budget) */}
          {tab === "viagem" && (
            <section className="stats-row">
              <div className="stat-card"><span className="stat-icon">⌁</span><div><small>Rota da operação</small><strong data-testid="stat-km">{calc.km.toFixed(0)}</strong><span>km planejados</span></div></div>
              <div className="stat-card"><span className="stat-icon green">◉</span><div><small>Equipe selecionada</small><strong data-testid="stat-equipe">{calc.teamQty}</strong><span>profissionais</span></div></div>
              <div className="stat-card"><span className="stat-icon">◷</span><div><small>Tempo em deslocamento</small><strong>{fmtDuration(calc.mins)}</strong><span>estimado</span></div></div>
              <div className="stat-card travel-cost-stat"><span className="stat-icon green">R$</span><div><small>Custo da viagem</small><strong data-testid="stat-travel">{money(calc.travelCost)}</strong><span>combustível + pedágios</span></div></div>
            </section>
          )}

          {tab === "dashboard" && <DashboardTab dashboard={dashboard} />}
          {tab === "viagem" && budgetStep === "viagem" && <ViagemTab {...{ f, upd, calc, vehicles, activeProfessionals, teamSel, toggleTeam, setTeamQty, buscaCep, setNumero, onVehicleChange, goToCosts, calcRouteAuto, routeLoading, routeData }} />}
          {tab === "viagem" && budgetStep === "remocao" && <RemocaoTab {...{ f, upd, calc, manualRate, setManualRate, currentPatient, budgetText, saveQuote, sendWhats, settings, selectedVehicle, currentQuoteId, quotes, setBudgetStep }} />}
          {tab === "orcamentos" && <OrcamentosTab {...{ quotes, role, startNew, editQuote, changeQuoteStatus, deleteQuote, convertQuote, settings }} />}
          {tab === "rotas" && <RotasTab {...{ transports, changeTransportStatus, deleteTransport, role }} />}
          {tab === "rastreamento" && <RastreamentoTab />}
          {tab === "pacientes" && <PacientesTab {...{ patients, reload: loadAll, selectPatient }} />}
          {tab === "profissionais" && <ProfissionaisTab {...{ professionals, reload: loadAll, role }} />}
          {tab === "veiculos" && <FrotaTab {...{ vehicles, maintenance, reload: loadAll, financeMonth, subInfo, goToAssinatura: () => setTab("assinatura") }} />}
          {tab === "financeiro" && <FinanceiroTab {...{ finance, financeMonth, setFinanceMonth, settings }} />}
          {tab === "assinatura" && <AssinaturaTab settings={settings} />}
          {tab === "eventos" && <EventosTab settings={settings} />}
          {tab === "servicos" && <ServicosTab settings={settings} role={role} />}
          {tab === "usuarios" && <UsuariosTab {...{ users, reload: () => api.get("/users").then((r) => setUsers(r.data)), currentUserId: user?.id }} />}
          {tab === "empresa" && <EmpresaTab {...{ settings, reload: loadAll }} />}
          {tab === "ajuda" && <AjudaTab comp={settings} />}
        </main>
        <footer><strong>RotaSaúde</strong><span>Planejamento que leva cuidado até você</span><em>Tecnologia a serviço da vida.</em></footer>
      </div>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
    </div>
  );
}

/* ============================ TAB COMPONENTS ============================ */

function DashboardTab({ dashboard }) {
  const [d, setD] = useState(dashboard || {});
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  const cards = [
    { l: "Remoções do mês", v: d.removalsMonth || 0, t: "dash-removals" },
    { l: "Faturamento realizado", v: money(d.revenueMonth || 0), t: "dash-revenue" },
    { l: "Km rodados", v: `${Number(d.kmMonth || 0).toFixed(0)} km`, t: "dash-km" },
    { l: "Lucro estimado", v: money(d.profitMonth || 0), t: "dash-profit" },
    { l: "Orçamentos pendentes", v: d.pendingQuotes || 0, t: "dash-pending" },
    { l: "Remoções agendadas", v: d.scheduledRemovals || 0, t: "dash-scheduled" },
  ];
  return (
    <>
      <div className="module-head"><div><h1>Painel da empresa</h1><p>Visão operacional do mês corrente. O faturamento considera apenas remoções realizadas.</p></div></div>
      <div className="finance-summary" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        {cards.map((c) => (<article key={c.l} className="report-card" data-testid={c.t}><span>{c.l}</span><strong>{c.v}</strong></article>))}
      </div>
      {(d.upcomingEvents?.length > 0) && (
        <article className="card" style={{ marginTop: 16 }} data-testid="dash-upcoming-events">
          <h2>Próximos eventos agendados</h2>
          <div className="module-list compact">
            {d.upcomingEvents.map((e, i) => (
              <div className="list-card" key={i} data-testid={`dash-event-${i}`}>
                <div>
                  <strong>{e.name}</strong>
                  <span>{e.date ? new Date(e.date + "T12:00:00").toLocaleDateString("pt-BR") : "—"} • {e.location || "Local não informado"}</span>
                  {e.vehicleName ? <small>{e.vehicleName}</small> : null}
                </div>
                <div className="list-value">{money(e.revenue)}</div>
              </div>
            ))}
          </div>
        </article>
      )}
    </>
  );
}

function ViagemTab({ f, upd, calc, vehicles, activeProfessionals, teamSel, toggleTeam, setTeamQty, buscaCep, setNumero, onVehicleChange, goToCosts, calcRouteAuto, routeLoading, routeData }) {
  const [clinicSug, setClinicSug] = useState([]);
  const [clinicOpen, setClinicOpen] = useState(false);
  const clinicTimer = useRef(null);
  const onClinicName = (val) => {
    upd("clinicaNome", val);
    clearTimeout(clinicTimer.current);
    if (val.trim().length < 2) { setClinicSug([]); setClinicOpen(false); return; }
    clinicTimer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/clinics?q=${encodeURIComponent(val.trim())}`); setClinicSug(data); setClinicOpen(true); }
      catch (e) { console.error("Busca de clínicas falhou:", e); }
    }, 300);
  };
  const pickClinic = (c) => {
    upd("clinicaNome", c.name); upd("clinicaEndereco", c.address);
    if (c.cep) upd("cepClinica", c.cep);
    setClinicOpen(false); setClinicSug([]);
  };
  const s = calc.schedule;
  const N = (k, extra = {}) => <input type="number" min="0" step={extra.step || "0.1"} value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`inp-${k}`} />;
  return (
    <section className="panel active">
      <div className="card" style={{ marginBottom: 12, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }} data-testid="service-bar">
        <label style={{ flex: "1 1 240px" }}>Tipo de serviço
          <select value={f.serviceType} onChange={(e) => upd("serviceType", e.target.value)} data-testid="inp-service-type">
            {SERVICE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </label>
        <label style={{ flex: "1 1 240px" }}>Tipo de rota
          <select value={f.routeMode} onChange={(e) => upd("routeMode", e.target.value)} data-testid="inp-route-mode">
            <option value="3pontos">3 pontos (Origem → Paciente → Clínica)</option>
            <option value="ponto_a_ponto">Ponto a ponto (Origem → Destino)</option>
          </select>
        </label>
      </div>
      <div className="dashboard-grid">
        <article className="card route-input-card">
          <h2><span>1</span> Dados da Rota</h2>
          <label>CEP de origem da remoção<div className="input-action"><input value={f.cepOrigem} maxLength={9} onChange={(e) => upd("cepOrigem", e.target.value)} placeholder="00000-000" /><input value={f.numOrigem || ""} onChange={setNumero("numOrigem", "origem")} placeholder="Nº" style={{ maxWidth: 80 }} data-testid="num-origem" /><button onClick={() => buscaCep("cepOrigem", "origem", "numOrigem")} data-testid="cep-origem">Buscar</button></div></label>
          <label>Origem da remoção<input value={f.origem} onChange={(e) => upd("origem", e.target.value)} data-testid="inp-origem" placeholder="Base, endereço ou local de saída da equipe" /></label>
          <div className="route-point-divider"><strong>Local onde o paciente está</strong></div>
          <label>CEP do paciente<div className="input-action"><input value={f.cepDestino} maxLength={9} onChange={(e) => upd("cepDestino", e.target.value)} placeholder="00000-000" /><input value={f.numDestino || ""} onChange={setNumero("numDestino", "destino")} placeholder="Nº" style={{ maxWidth: 80 }} data-testid="num-destino" /><button onClick={() => buscaCep("cepDestino", "destino", "numDestino")} data-testid="cep-destino">Buscar</button></div></label>
          <label>{f.routeMode === "ponto_a_ponto" ? "Destino do traslado" : "Destino / local do paciente"}<input value={f.destino} onChange={(e) => upd("destino", e.target.value)} data-testid="inp-destino" placeholder="Rua, número, bairro, cidade, UF e CEP" /></label>
          {f.routeMode !== "ponto_a_ponto" && (<>
          <div className="route-point-divider clinic"><strong>Unidade de internação</strong></div>
          <label>Unidade de internação / Clínica
            <div className="clinic-ac">
              <input value={f.clinicaNome} onChange={(e) => onClinicName(e.target.value)} onFocus={() => clinicSug.length && setClinicOpen(true)} onBlur={() => setTimeout(() => setClinicOpen(false), 180)} data-testid="inp-clinica" placeholder="Digite o nome — buscamos clínicas já cadastradas" autoComplete="off" />
              {clinicOpen && clinicSug.length > 0 && (
                <div className="clinic-ac-list" data-testid="clinic-suggestions">
                  {clinicSug.map((c) => (
                    <button type="button" key={c.id} className="clinic-ac-item" onMouseDown={() => pickClinic(c)} data-testid={`clinic-sug-${c.id}`}>
                      <strong>{c.name}</strong><small>{c.address}{c.city ? ` • ${c.city}${c.uf ? `/${c.uf}` : ""}` : ""}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label>CEP da clínica<div className="input-action"><input value={f.cepClinica} maxLength={9} onChange={(e) => upd("cepClinica", e.target.value)} placeholder="00000-000" /><input value={f.numClinica || ""} onChange={setNumero("numClinica", "clinicaEndereco")} placeholder="Nº" style={{ maxWidth: 80 }} data-testid="num-clinica" /><button onClick={() => buscaCep("cepClinica", "clinicaEndereco", "numClinica")} data-testid="cep-clinica">Buscar</button></div></label>
          <label>Endereço completo da clínica<input value={f.clinicaEndereco} onChange={(e) => upd("clinicaEndereco", e.target.value)} data-testid="inp-clinica-end" placeholder="Rua, número, bairro, cidade, UF e CEP" /></label>
          </>)}
          <div className="subsection-title">2. Data e Horários</div>
          <div className="split-fields">
            <label>Data/hora de saída<input type="datetime-local" value={f.saida} onChange={(e) => upd("saida", e.target.value)} /></label>
            <label>Tempo no local do paciente<input type="number" min="0" step="5" value={f.permanencia} onChange={(e) => upd("permanencia", e.target.value)} /><small>minutos para abordagem/embarque</small></label>
            {f.routeMode !== "ponto_a_ponto" && <label>Tempo na clínica<input type="number" min="0" step="5" value={f.permanenciaClinica} onChange={(e) => upd("permanenciaClinica", e.target.value)} /><small>minutos estimados na admissão</small></label>}
          </div>
          <label className="check-line"><input type="checkbox" checked={f.idaVolta} onChange={(e) => upd("idaVolta", e.target.checked)} /> {f.routeMode === "ponto_a_ponto" ? "Incluir retorno ao ponto de origem" : "Incluir retorno da clínica à origem"}</label>
        </article>

        <article className="card route-card">
          <div className="card-head"><h2><span>3</span> Rota e Distância</h2><button className="primary" onClick={calcRouteAuto} disabled={routeLoading} data-testid="btn-calc-route">{routeLoading ? "Calculando…" : "Calcular automaticamente"}</button></div>
          {routeData?.points ? (
            <div className="route-map-wrap">
              <RouteMap points={routeData.points} polyline={routeData.polyline} height={340} />
              <div className="api-badge">{f.routeMode === "ponto_a_ponto" ? "Trajeto Origem → Destino" : "Trajeto Origem → Paciente → Clínica"}</div>
            </div>
          ) : (
            <div className="map-placeholder">
              <div className="map-grid" />
              <svg viewBox="0 0 800 320" preserveAspectRatio="none" aria-hidden="true">
                <path d="M70,230 C180,185 215,250 315,190 S470,210 560,135 S690,160 748,78" fill="none" stroke="#d5e6f6" strokeWidth="18" strokeLinecap="round" />
                <path d="M70,230 C180,185 215,250 315,190 S470,210 560,135 S690,160 748,78" fill="none" stroke="#1671d9" strokeWidth="8" strokeLinecap="round" />
                <circle cx="70" cy="230" r="14" fill="#27b35f" /><circle cx="405" cy="188" r="14" fill="#f0a52b" /><circle cx="748" cy="78" r="14" fill="#e54545" />
              </svg>
              <div className="map-label origin-label">{f.origem || "Origem"}</div>
              <div className="map-label patient-label">{f.destino || "Paciente"}</div>
              <div className="map-label dest-label">{f.clinicaNome || "Clínica"}</div>
              <div className="api-badge">Km, tempo e pedágio automáticos via Google Maps</div>
            </div>
          )}
          <div className="route-leg-grid">
            <div className="route-leg-card"><h3>{f.routeMode === "ponto_a_ponto" ? "1. Origem → Destino" : "1. Origem → Paciente"}</h3><div className="route-leg-fields"><label>Km{N("kmIda")}</label><label>Minutos{N("tempoIda", { step: "1" })}</label><label>Pedágio (R$){N("pedagio", { step: "0.01" })}</label></div></div>
            {f.routeMode !== "ponto_a_ponto" && <div className="route-leg-card"><h3>2. Paciente → Clínica</h3><div className="route-leg-fields"><label>Km{N("kmPacienteClinica")}</label><label>Minutos{N("tempoPacienteClinica", { step: "1" })}</label><label>Pedágio (R$){N("pedagioPacienteClinica", { step: "0.01" })}</label></div></div>}
            <div className="route-leg-card return-leg"><h3>{f.routeMode === "ponto_a_ponto" ? "2. Destino → Origem (retorno)" : "3. Clínica → Origem"}</h3><div className="route-leg-fields"><label>Km{N("kmRetorno")}</label><label>Minutos{N("tempoRetorno", { step: "1" })}</label><label>Pedágio (R$){N("pedagioRetorno", { step: "0.01" })}</label></div></div>
          </div>
          <div className="route-summary-grid">
            <div><small>Km total da operação</small><strong>{calc.km.toFixed(1)} km</strong></div>
            <div><small>Tempo em deslocamento</small><strong>{fmtDuration(calc.mins)}</strong></div>
            <div><small>Pedágios totais</small><strong>{money(calc.ped)}</strong></div>
          </div>
          {(f.origem && f.destino && f.clinicaEndereco) && (
            <a className="secondary nav-gmaps-btn" href={buildNavUrl({ origin: f.origem, patient: f.destino, clinic: f.clinicaEndereco })} target="_blank" rel="noreferrer" data-testid="btn-open-navigation">Abrir navegação no Google Maps</a>
          )}
        </article>

        <article className="card team-card">
          <div className="card-head"><h2><span>4</span> Profissionais</h2></div>
          <p className="hint">Selecione os profissionais cadastrados e informe a quantidade. Os valores são definidos na aba Profissionais.</p>
          <div className="team-list">
            {activeProfessionals.length === 0 && <p className="muted-note">Cadastre profissionais na aba Profissionais.</p>}
            {activeProfessionals.map((pro) => {
              const sel = teamSel[pro.id] || { checked: false, qty: 1 };
              const sub = sel.checked ? Math.max(1, n(sel.qty) || 1) * (Number(pro.value) || 0) : 0;
              return (
                <div className="team-row team-row-fixed" key={pro.id}>
                  <input type="checkbox" className="team-check" checked={sel.checked} onChange={(e) => toggleTeam(pro.id, e.target.checked)} data-testid={`team-check-${pro.id}`} />
                  <span className="name">{pro.name}</span>
                  <label>Qtd.<input className="team-qty" type="number" min="1" step="1" value={sel.qty} onChange={(e) => setTeamQty(pro.id, e.target.value)} /></label>
                  <div className="team-fixed-value"><small>Valor unit.</small><strong>{money(pro.value)}</strong></div>
                  <strong className="team-subtotal">{money(sub)}</strong>
                </div>
              );
            })}
          </div>
        </article>

        <article className="card vehicle-card">
          <h2><span>5</span> Veículo e Consumo</h2>
          <div className="split-fields">
            <label>Veículo<select value={f.veiculo} onChange={(e) => onVehicleChange(e.target.value)} data-testid="inp-veiculo"><option value="">Selecione um veículo da frota</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` • ${v.plate}` : ""}</option>)}</select></label>
            <label>Consumo médio (km/L)<input type="number" value={f.consumo} readOnly /></label>
            <label>Combustível (R$/L)<input type="number" value={f.precoComb} readOnly /></label>
            <label>Tipo p/ pedágio<select value={f.tipoVeiculo} onChange={(e) => upd("tipoVeiculo", e.target.value)} data-testid="inp-tipo-veiculo"><option value="carro">Carro / Ambulância (2 eixos)</option><option value="van">Van / Utilitário (2 eixos)</option><option value="caminhao3">Caminhão 3 eixos</option><option value="caminhao4">Caminhão 4 eixos</option></select></label>
          </div>
          <div className="vehicle-metrics">
            <div><small>Litros estimados</small><strong>{calc.litros.toFixed(1)} L</strong></div>
            <div><small>Combustível</small><strong>{money(calc.comb)}</strong></div>
          </div>
        </article>

        <article className="card schedule-card">
          <h2><span>6</span> Previsão de Horários</h2>
          <div className="schedule-columns three-schedule">
            <div className="schedule-block"><h3>{f.routeMode === "ponto_a_ponto" ? "Origem → Destino" : "Origem → Paciente"}</h3><p><span>Saída da origem</span><strong>{fmtTime(s.s)}</strong></p><p><span>Duração</span><strong>{fmtDuration(s.t1)}</strong></p><p className="success"><span>{f.routeMode === "ponto_a_ponto" ? "Chegada ao destino" : "Chegada ao paciente"}</span><strong>{fmtTime(s.chegadaPaciente, true)}</strong></p></div>
            {f.routeMode !== "ponto_a_ponto" && <div className="schedule-block"><h3>Paciente → Clínica</h3><p><span>Saída com paciente</span><strong>{fmtTime(s.saidaPaciente)}</strong></p><p><span>Duração</span><strong>{fmtDuration(s.t2)}</strong></p><p className="success"><span>Chegada à clínica</span><strong>{fmtTime(s.chegadaClinica, true)}</strong></p></div>}
            <div className="schedule-block"><h3>Retorno</h3><p><span>{f.routeMode === "ponto_a_ponto" ? "Saída do destino" : "Saída da clínica"}</span><strong>{s.rt ? fmtTime(s.saidaClinica) : "Sem retorno"}</strong></p><p><span>Duração</span><strong>{s.rt ? fmtDuration(s.t3) : "—"}</strong></p><p className="info"><span>Chegada à origem</span><strong>{s.rt ? fmtTime(s.base, true) : "—"}</strong></p></div>
          </div>
        </article>
      </div>
      <div className="continue-bar"><div className="continue-context"><strong>Rota pronta?</strong><span>Revise os trechos, veículo e equipe antes de avançar.</span></div><button className="primary large" onClick={goToCosts} data-testid="btn-continue-costs">Continuar para custos e orçamento →</button></div>
    </section>
  );
}

function RemocaoTab({ f, upd, calc, manualRate, setManualRate, currentPatient, budgetText, saveQuote, sendWhats, settings, selectedVehicle, currentQuoteId, setBudgetStep }) {
  const M = (k, step = "0.01") => <input type="number" min="0" step={step} value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`cost-${k}`} />;
  const custoKmVal = manualRate ? f.custoKm : calc.valorKm.toFixed(2);
  const pdfBudget = { patientData: currentPatient, family: f.familia, date: f.dataRemocao, origin: f.orcOrigem || f.origem, patientDestination: f.destino, clinicName: f.clinicaNome, clinicAddress: f.clinicaEndereco, km: calc.km, value: calc.recomendado, team: calc.teamNames, vehicleName: selectedVehicle?.name };
  return (
    <section className="panel active">
      <div className="flex-between" style={{ marginBottom: 12 }}>
        <button className="secondary" onClick={() => setBudgetStep("viagem")} style={{ border: 0, borderRadius: 8, padding: "10px 14px", fontWeight: 800, cursor: "pointer" }} data-testid="btn-back-route">← Voltar à rota</button>
        <span className="muted-note">{currentQuoteId ? "Editando orçamento existente" : "Novo orçamento"}</span>
      </div>
      <div className="budget-grid">
        <article className="card">
          <h2><span>7</span> Custos da Remoção</h2>
          <div className="form-grid">
            <label>Km total da operação<input type="number" value={calc.km.toFixed(1)} readOnly /></label>
            <label>Valor final por km rodado (R$/km)<div className="input-with-button"><input type="number" min="0" step="0.01" value={custoKmVal} onChange={(e) => { setManualRate(true); upd("custoKm", e.target.value); }} data-testid="cost-custoKm" /><button type="button" className="secondary mini-btn" onClick={() => setManualRate(false)} data-testid="btn-auto-km">Automático</button></div><small>Automático = valor final ÷ km total. Já inclui combustível, pedágios, profissionais, despesas, lucro e margem.</small></label>
            <label>Combustível (R$)<input type="number" value={calc.cComb.toFixed(2)} readOnly /></label>
            <label>Pedágios (R$)<input type="number" value={calc.cPed.toFixed(2)} readOnly /></label>
            <label>Alimentação (R$){M("alimentacao")}</label>
            <label>Hospedagem (R$){M("hospedagem")}</label>
            <label>Estacionamento (R$){M("estacionamento")}</label>
            <label>Outras despesas (R$){M("outras")}</label>
          </div>
        </article>

        <article className="card result-card">
          <h2><span>8</span> Custos e Resultado</h2>
          <div className="result-line"><span>Combustível</span><strong>{money(calc.cComb)}</strong></div>
          <div className="result-line"><span>Pedágios</span><strong>{money(calc.cPed)}</strong></div>
          <div className="result-line"><span>Profissionais</span><strong>{money(calc.teamCost)}</strong></div>
          <div className="result-line"><span>Outros custos</span><strong>{money(calc.misc)}</strong></div>
          <div className="result-line"><span>Custo total</span><strong>{money(calc.costBase)}</strong></div>
          <div className="result-line highlight-line"><span>Preço calculado</span><strong>{money(calc.venda)}</strong></div>
          <div className="result-line"><span>Lucro estimado</span><strong>{money(calc.lucro)}</strong></div>
          <div className="result-line"><span>Margem efetiva</span><strong>{calc.margemEf.toFixed(1)}%</strong></div>
          <div className="final-price"><span>Valor final da remoção</span><strong data-testid="valor-final">{money(calc.recomendado)}</strong></div>
        </article>

        <article className="card price-card">
          <h2><span>9</span> Preço e Margem</h2>
          <div className="form-grid">
            <label>Modelo de lucro<select value={f.modeloMargem} onChange={(e) => upd("modeloMargem", e.target.value)}><option value="margem">Margem real sobre venda</option><option value="markup">Acréscimo sobre custo</option></select></label>
            <label>Percentual (%)<input type="number" min="0" max="95" step="0.1" value={f.margem} onChange={(e) => upd("margem", e.target.value)} /></label>
            <label>Valor mínimo da remoção (R$)<input type="number" min="0" step="0.01" value={f.valorMinimo} onChange={(e) => upd("valorMinimo", e.target.value)} /></label>
            <label>Adicional comercial (R$)<input type="number" min="0" step="0.01" value={f.adicional} onChange={(e) => upd("adicional", e.target.value)} /></label>
          </div>
        </article>

        <article className="card family-card">
          <h2><span>10</span> Orçamento para a Família</h2>
          <div className="form-grid">
            <label>Família/Responsável<input value={f.familia} onChange={(e) => upd("familia", e.target.value)} placeholder="Opcional" /></label>
            <label>Telefone / WhatsApp<input value={f.telefoneWhats} onChange={(e) => upd("telefoneWhats", e.target.value)} data-testid="inp-whats" placeholder="(11) 99999-9999" /></label>
            <label>Data da remoção<input type="date" value={f.dataRemocao} onChange={(e) => upd("dataRemocao", e.target.value)} /></label>
            <label>Origem<input value={f.orcOrigem} onChange={(e) => upd("orcOrigem", e.target.value)} placeholder="Cidade / endereço" /></label>
            <label>Destino da rota<input value={f.destino} readOnly /></label>
            <label>Unidade de internação / Clínica<input value={f.clinicaNome} readOnly /></label>
            <label className="full-span">Endereço completo da unidade<input value={f.clinicaEndereco} readOnly /></label>
          </div>
          <textarea rows={10} value={budgetText} readOnly />
          <div className="action-grid four-actions">
            <button className="primary" onClick={saveQuote} data-testid="btn-save-quote">Salvar orçamento</button>
            <button className="pdf-btn" onClick={() => generateQuotePDF(pdfBudget, settings)} data-testid="btn-pdf-quote">Gerar PDF</button>
            <button className="whatsapp" onClick={sendWhats} data-testid="btn-whats">WhatsApp</button>
            <button className="secondary" onClick={() => { navigator.clipboard.writeText(budgetText); toast.success("Texto copiado!"); }}>Copiar texto</button>
          </div>
        </article>
      </div>
    </section>
  );
}

function StatusPill({ v, label }) { return <span className={`status-pill st-${v}`}>{label}</span>; }

function OrcamentosTab({ quotes, role, startNew, editQuote, changeQuoteStatus, deleteQuote, convertQuote, settings }) {
  const canEdit = ["admin", "operacional"].includes(role);
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Meus Orçamentos</h1><p>Orçamentos só entram no Financeiro quando a <strong>remoção</strong> é marcada como <strong>realizada</strong>.</p></div>{canEdit && <button className="primary" onClick={startNew} data-testid="btn-new-quote">＋ Novo orçamento</button>}</div>
      <div className="module-list">
        {quotes.length === 0 && <div className="empty-state"><strong>Nenhum orçamento salvo</strong><span>Salve um orçamento para ele aparecer aqui.</span></div>}
        {quotes.map((b) => (
          <div className="list-card" key={b.id} data-testid={`quote-${b.id}`}>
            <div>
              <strong>{b.family}</strong>
              <span>{b.origin} → {b.patientDestination || b.destination} → {b.clinicName || b.clinicAddress || "Clínica"}</span>
              <small>{b.date ? new Date(b.date + "T12:00:00").toLocaleDateString("pt-BR") : "Sem data"} • {Number(b.km || 0).toFixed(1)} km • <span className="budget-km-badge">{money((Number(b.value) || 0) / (Number(b.km) || 1))}/km</span></small>
              <StatusPill v={b.status} label={qLabel(b.status)} />
            </div>
            <div className="list-value">
              <strong>{money(b.value)}</strong>
              {canEdit && (
                <select className="budget-status-select" value={b.status} onChange={(e) => changeQuoteStatus(b.id, e.target.value)} data-testid={`quote-status-${b.id}`}>
                  {QUOTE_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              )}
              {canEdit && <button onClick={() => editQuote(b)} data-testid={`quote-edit-${b.id}`}>Editar</button>}
              <button onClick={() => generateQuotePDF(b, settings)} data-testid={`quote-pdf-${b.id}`}>PDF</button>
              {canEdit && !b.transportId && <button onClick={() => convertQuote(b.id)} data-testid={`quote-convert-${b.id}`}>Converter em Remoção</button>}
              {canEdit && b.transportId && <span className="muted-note">Convertido ✓</span>}
              {canEdit && <button className="danger-lite" onClick={() => deleteQuote(b.id)}>Excluir</button>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RastreamentoTab() {
  const [units, setUnits] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let stop = false;
    const load = async () => { try { const { data } = await api.get("/tracking/active"); if (!stop) { setUnits(data); setLoading(false); } } catch { if (!stop) setLoading(false); } };
    load();
    const id = setInterval(load, 8000);
    return () => { stop = true; clearInterval(id); };
  }, []);
  const now = Date.now();
  const enriched = units.map((u) => {
    const t = u.location?.updatedAt ? new Date(u.location.updatedAt).getTime() : 0;
    const ageMs = t ? now - t : Infinity;
    return { ...u, stale: !u.location || ageMs > 180000, minsAgo: t ? Math.floor(ageMs / 60000) : null };
  });
  const staleCount = enriched.filter((u) => u.stale).length;
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Rastreamento da frota</h1><p>Ambulâncias em operação em tempo real (atualiza a cada 8s). O ícone aponta a direção do veículo.</p></div></div>
      {staleCount > 0 && <div className="fleet-alert" data-testid="fleet-alert">⚠ {staleCount} ambulância(s) sem sinal de GPS há mais de 3 min — verifique com o motorista.</div>}
      <div className="fleet-wrap">
        <div className="fleet-map" data-testid="fleet-map-wrap"><FleetMap units={enriched} focusId={focusId} height={540} /></div>
        <div className="fleet-list" data-testid="fleet-list">
          {!loading && enriched.length === 0 && <div className="empty-state"><strong>Nenhuma ambulância ativa</strong><span>Gere o link do motorista em "Rotas e Viagens" e peça para ele compartilhar a localização.</span></div>}
          {enriched.map((u, i) => (
            <button key={u.transportId} className={`fleet-item ${u.stale ? "fleet-item-stale" : ""}`} onClick={() => setFocusId(u.transportId)} data-testid={`fleet-item-${u.transportId}`}>
              <span className={`fleet-num ${u.stale ? "fleet-num-stale" : ""}`}>{i + 1}</span>
              <span className="fleet-info">
                <strong>{u.label}</strong>
                <small>{u.route}</small>
                <small className="fleet-meta">
                  <StatusPill v={u.status} label={tLabel(u.status)} />
                  {u.stale
                    ? <span className="fleet-warn" data-testid={`fleet-warn-${u.transportId}`}>⚠ sem sinal {u.minsAgo != null ? `há ${u.minsAgo} min` : "(sem GPS)"}</span>
                    : <span>• atualizado {new Date(u.location.updatedAt).toLocaleTimeString("pt-BR")}</span>}
                </small>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function RotasTab({ transports, changeTransportStatus, deleteTransport, role }) {
  const canEdit = ["admin", "operacional"].includes(role);
  const canDelete = role === "admin";
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Rotas e Viagens</h1><p>Remoções agendadas e realizadas. Gere o link do motorista e acompanhe a localização ao vivo.</p></div></div>
      <div className="module-list">
        {transports.length === 0 && <div className="empty-state"><strong>Nenhuma remoção</strong><span>Converta um orçamento aprovado em remoção na aba Meus Orçamentos.</span></div>}
        {transports.map((t) => <TransportCard key={t.id} t={t} changeTransportStatus={changeTransportStatus} deleteTransport={deleteTransport} canEdit={canEdit} canDelete={canDelete} />)}
      </div>
    </section>
  );
}

function TransportCard({ t, changeTransportStatus, deleteTransport, canEdit, canDelete }) {
  const [live, setLive] = useState(false);
  const [loc, setLoc] = useState(null);
  const [link, setLink] = useState(t.driverToken ? `${window.location.origin}/motorista/${t.driverToken}` : null);
  const [busy, setBusy] = useState(false);
  const points = t.form?.routePoints || t.mapData?.points || null;
  const polyline = t.form?.routePolyline || t.mapData?.polyline || null;
  const fuel = Number(t.costs?.fuel || 0), toll = Number(t.costs?.tolls || 0);

  const genLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/transports/${t.id}/driver-link`);
      const url = `${window.location.origin}${data.path}`;
      setLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Link do motorista copiado para a área de transferência!"); }
      catch { toast.success("Link do motorista gerado."); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!live) return;
    let stop = false;
    const poll = async () => { try { const { data } = await api.get(`/transports/${t.id}/location`); if (!stop) setLoc(data.location); } catch { /* ignore */ } };
    poll();
    const id = setInterval(poll, 8000);
    return () => { stop = true; clearInterval(id); };
  }, [live, t.id]);

  const waUrl = link ? `https://wa.me/?text=${encodeURIComponent(`Rota de remoção — abra no seu celular: ${link}`)}` : null;

  return (
    <div className="list-card" style={{ flexDirection: "column", alignItems: "stretch" }} data-testid={`transport-${t.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <strong>{[t.route?.origin, t.route?.patientDestination, t.route?.clinicName || t.route?.clinicAddress].filter(Boolean).join(" → ")}</strong>
          <span><b>{serviceLabel(t.serviceType)}</b> • {t.family || "—"} • {Number(t.km || 0).toFixed(1)} km</span>
          <small className="route-cost-breakdown">Combustível: {money(fuel)} • Pedágios: {money(toll)} • <b>Custo da viagem: {money(fuel + toll)}</b></small>
          <StatusPill v={t.status} label={tLabel(t.status)} />
        </div>
        <div className="list-value route-list-actions">
          <strong>{money(t.revenue)}</strong>
          {canEdit && (
            <select className="budget-status-select" value={t.status} onChange={(e) => changeTransportStatus(t.id, e.target.value)} data-testid={`transport-status-${t.id}`}>
              {TRANSPORT_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="route-live-actions">
        {canEdit && <button onClick={genLink} disabled={busy} data-testid={`btn-driver-link-${t.id}`}>{busy ? "Gerando…" : (link ? "Copiar link do motorista" : "Gerar link do motorista")}</button>}
        {link && waUrl && <a href={waUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}><button data-testid={`btn-driver-wa-${t.id}`}>Enviar no WhatsApp</button></a>}
        <button onClick={() => setLive((v) => !v)} data-testid={`btn-live-${t.id}`}>{live ? "Ocultar mapa ao vivo" : "Acompanhar ao vivo"}</button>
        {canDelete && <button onClick={() => deleteTransport(t.id)} data-testid={`btn-delete-transport-${t.id}`} style={{ color: "#b53b3b" }}>Excluir</button>}
      </div>
      {live && (
        <div style={{ marginTop: 10 }}>
          <div className="route-map-wrap" style={{ height: 320 }}>
            <RouteMap points={points} polyline={polyline} driverLocation={loc} height={320} />
          </div>
          <p style={{ fontSize: 12, color: "#6d8094", marginTop: 6 }}>
            {loc ? `Última posição do motorista às ${new Date(loc.updatedAt).toLocaleTimeString("pt-BR")}` : "Aguardando o motorista abrir o link e compartilhar a localização…"}
          </p>
        </div>
      )}
    </div>
  );
}

function PacientesTab({ patients, reload, selectPatient }) {
  const empty = { id: null, family: "", phone: "", patient: "", age: "", height: "", weight: "", usesMedication: "Não", medications: "", notes: "" };
  const [form, setForm] = useState(empty);
  const sf = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.family.trim()) return toast.error("Informe o nome da família ou responsável.");
    if (!form.patient.trim()) return toast.error("Informe o nome do paciente.");
    const { id, ...body } = form;
    try {
      if (id) await api.patch(`/patients/${id}`, body); else await api.post("/patients", body);
      toast.success(id ? "Cadastro atualizado." : "Cadastro salvo."); setForm(empty); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (p) => { if (!window.confirm("Excluir este cadastro?")) return; try { await api.delete(`/patients/${p.id}`); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const yes = form.usesMedication === "Sim";
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Pacientes e Responsáveis</h1><p>Cadastre os dados da família e do paciente para reutilizar nos orçamentos e no WhatsApp.</p></div></div>
      <div className="module-grid two-col">
        <article className="card">
          <h2>{form.id ? "Editar cadastro" : "Novo cadastro"}</h2>
          <div className="form-grid">
            <label>Família / Responsável<input value={form.family} onChange={sf("family")} data-testid="pac-family" placeholder="Nome do familiar ou responsável" /></label>
            <label>Telefone / WhatsApp<input value={form.phone} onChange={sf("phone")} placeholder="(11) 99999-9999" /></label>
            <label>Paciente<input value={form.patient} onChange={sf("patient")} data-testid="pac-patient" placeholder="Nome do paciente" /></label>
            <label>Idade<input type="number" value={form.age} onChange={sf("age")} placeholder="Ex.: 32" /></label>
            <label>Altura<input value={form.height} onChange={sf("height")} placeholder="Ex.: 1,75 m" /></label>
            <label>Peso<input value={form.weight} onChange={sf("weight")} placeholder="Ex.: 78 kg" /></label>
            <label>Faz uso de medicamentos?<select value={form.usesMedication} onChange={sf("usesMedication")}><option value="Não">Não</option><option value="Sim">Sim</option></select></label>
            <label className={yes ? "" : "field-disabled"}>Se sim, quais medicamentos?<input value={form.medications} onChange={sf("medications")} disabled={!yes} placeholder="Nome e dosagem, se souber" /></label>
          </div>
          <label className="full-label">Outras observações clínicas<textarea rows={4} value={form.notes} onChange={sf("notes")} placeholder="Informações clínicas relevantes para a remoção" /></label>
          <div className="inline-actions"><button className="primary" onClick={save} data-testid="pac-save">{form.id ? "Atualizar cadastro" : "Salvar cadastro"}</button>{form.id && <button className="secondary" onClick={() => setForm(empty)}>Cancelar edição</button>}</div>
        </article>
        <article className="card"><h2>Cadastros</h2>
          <div className="module-list compact">
            {patients.length === 0 && <div className="empty-state"><strong>Nenhum paciente cadastrado</strong><span>Cadastre a família e o paciente.</span></div>}
            {patients.map((p) => (
              <div className="list-card patient-card" key={p.id} data-testid={`patient-${p.id}`}>
                <div><strong>{p.patient || "Paciente"}</strong><span>Família: {p.family || "—"} • {p.phone || "Sem telefone"}</span><small>{[p.age ? `${p.age} anos` : "", p.height, p.weight, `Medicamentos: ${p.usesMedication || "Não"}`].filter(Boolean).join(" • ")}</small></div>
                <div className="list-value"><button onClick={() => selectPatient(p)} data-testid={`patient-use-${p.id}`}>Usar no orçamento</button><button onClick={() => setForm({ ...empty, ...p })}>Editar</button><button className="danger-lite" onClick={() => del(p)}>Excluir</button></div>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function ProfissionaisTab({ professionals, reload, role }) {
  const [nome, setNome] = useState(""); const [valor, setValor] = useState("0");
  const canEdit = role === "admin";
  const add = async () => {
    if (!nome.trim()) return toast.error("Informe o nome/especialidade.");
    try { await api.post("/professionals", { name: nome.trim(), value: Number(valor) || 0, active: true }); setNome(""); setValor("0"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updVal = async (p, value) => { try { await api.patch(`/professionals/${p.id}`, { value: Number(value) || 0 }); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const del = async (p) => { if (!window.confirm(`Excluir ${p.name}?`)) return; try { await api.delete(`/professionals/${p.id}`); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Profissionais</h1><p>Cadastre os tipos de profissionais e os valores padrão por remoção.</p></div></div>
      <div className="module-grid two-col">
        {canEdit && (
          <article className="card">
            <h2>Adicionar novo profissional</h2>
            <div className="form-grid">
              <label>Profissional / especialidade<input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="prof-name" placeholder="Ex.: Cuidador, Fisioterapeuta" /></label>
              <label>Valor padrão (R$)<input type="number" min="0" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} data-testid="prof-value" /></label>
            </div>
            <button className="primary" onClick={add} data-testid="prof-add">＋ Adicionar profissional</button>
          </article>
        )}
        <article className="card">
          <div className="card-head"><h2>Equipe cadastrada</h2></div>
          <div className="professional-config-list">
            {professionals.map((p) => (
              <div className="professional-config-row" key={p.id} data-testid={`prof-${p.id}`}>
                <div><strong>{p.name}</strong><small>Valor padrão por profissional</small></div>
                <input type="number" min="0" step="0.01" defaultValue={p.value} disabled={!canEdit} onBlur={(e) => canEdit && Number(e.target.value) !== p.value && updVal(p, e.target.value)} />
                {canEdit && <button className="danger-lite" onClick={() => del(p)}>Excluir</button>}
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function FrotaTab({ vehicles, maintenance, reload, financeMonth, subInfo, goToAssinatura }) {
  const emptyV = { id: null, name: "", consumption: "10", fuel: "6.20", plate: "" };
  const [v, setV] = useState(emptyV);
  const maxV = subInfo?.plan?.maxVehicles;
  const usedV = vehicles.length;
  const atLimit = !!maxV && usedV >= maxV && !v.id;
  const [m, setM] = useState({ vehicleId: "", date: today(), type: "Troca de óleo", value: "0", description: "" });
  const saveV = async () => {
    if (!v.name.trim()) return toast.error("Informe um nome para o veículo.");
    const body = { name: v.name.trim(), consumption: Number(v.consumption) || 10, fuel: Number(v.fuel) || 0, plate: v.plate.trim() };
    try { if (v.id) await api.patch(`/vehicles/${v.id}`, body); else await api.post("/vehicles", body); setV(emptyV); reload(); toast.success("Veículo salvo."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delV = async (x) => { if (!window.confirm("Excluir este veículo?")) return; try { await api.delete(`/vehicles/${x.id}`); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const addM = async () => {
    if (Number(m.value) <= 0) return toast.error("Informe o valor do gasto.");
    const vn = vehicles.find((x) => String(x.id) === String(m.vehicleId))?.name || "";
    try { await api.post("/maintenance", { ...m, value: Number(m.value), vehicleName: vn }); setM({ ...m, value: "0", description: "" }); reload(); toast.success("Lançamento salvo."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delM = async (x) => { if (!window.confirm("Excluir este lançamento?")) return; try { await api.delete(`/maintenance/${x.id}`); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const monthItems = maintenance.filter((x) => String(x.date || "").slice(0, 7) === financeMonth);
  const monthTotal = monthItems.reduce((s, x) => s + (Number(x.value) || 0), 0);
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Frota</h1><p>Cadastre veículos, consumo e acompanhe os gastos de manutenção.</p></div></div>
      <div className="module-grid two-col">
        <article className="card"><h2>{v.id ? "Editar veículo" : "Novo veículo"}</h2>
          {atLimit && (
            <div className="fleet-alert" data-testid="vehicle-limit-note" style={{ marginBottom: 12 }}>
              ⚠ Você atingiu o limite de {maxV} veículo(s) do seu plano. Faça upgrade para cadastrar mais.
              {goToAssinatura && <button className="tb-btn" style={{ marginLeft: 8 }} onClick={goToAssinatura} data-testid="vehicle-upgrade-cta">Ver planos</button>}
            </div>
          )}
          <div className="form-grid">
            <label>Nome / identificação<input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="veh-name" placeholder="Ex.: Van 01" /></label>
            <label>Consumo médio (km/L)<input type="number" step="0.1" value={v.consumption} onChange={(e) => setV({ ...v, consumption: e.target.value })} /></label>
            <label>Combustível (R$/L)<input type="number" step="0.01" value={v.fuel} onChange={(e) => setV({ ...v, fuel: e.target.value })} /></label>
            <label>Placa / observação<input value={v.plate} onChange={(e) => setV({ ...v, plate: e.target.value })} placeholder="Opcional" /></label>
          </div>
          <div className="inline-actions"><button className="primary" onClick={saveV} disabled={atLimit} data-testid="veh-save">Salvar veículo</button>{v.id && <button className="secondary" onClick={() => setV(emptyV)}>Cancelar edição</button>}</div>
        </article>
        <article className="card"><div className="card-head"><h2>Veículos cadastrados</h2>{maxV ? <strong data-testid="vehicle-usage">{usedV}/{maxV}</strong> : <strong data-testid="vehicle-usage">{usedV}</strong>}</div>
          <div className="module-list compact">
            {vehicles.length === 0 && <div className="empty-state"><strong>Nenhum veículo</strong><span>Cadastre consumo e combustível.</span></div>}
            {vehicles.map((x) => (
              <div className="list-card" key={x.id} data-testid={`vehicle-${x.id}`}><div><strong>{x.name}</strong><span>{x.consumption} km/L • {money(x.fuel)}/L</span><small>{x.plate}</small></div><div className="list-value"><button onClick={() => setV({ id: x.id, name: x.name, consumption: String(x.consumption), fuel: String(x.fuel), plate: x.plate || "" })}>Editar</button><button className="danger-lite" onClick={() => delV(x)}>Excluir</button></div></div>
            ))}
          </div>
        </article>
      </div>
      <div className="module-grid two-col fleet-maint-grid">
        <article className="card"><h2>Lançar manutenção / gasto da frota</h2>
          <div className="form-grid">
            <label>Veículo<select value={m.vehicleId} onChange={(e) => setM({ ...m, vehicleId: e.target.value })}><option value="">Selecione</option>{vehicles.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}<option value="outro">Outro / não cadastrado</option></select></label>
            <label>Data<input type="date" value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} /></label>
            <label>Tipo<select value={m.type} onChange={(e) => setM({ ...m, type: e.target.value })}>{["Troca de óleo", "Mecânica", "Pneus", "Freios", "Revisão preventiva", "Lavagem / higienização", "Seguro / documentação", "Outro"].map((o) => <option key={o}>{o}</option>)}</select></label>
            <label>Valor (R$)<input type="number" min="0" step="0.01" value={m.value} onChange={(e) => setM({ ...m, value: e.target.value })} data-testid="maint-value" /></label>
          </div>
          <label className="full-label">Descrição<input value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} placeholder="Ex.: troca de óleo e filtro" /></label>
          <button className="primary" onClick={addM} data-testid="maint-add">Salvar lançamento</button>
        </article>
        <article className="card"><div className="card-head"><h2>Histórico de manutenção</h2><strong>{money(monthTotal)}</strong></div>
          <div className="module-list compact">
            {maintenance.length === 0 && <div className="empty-state"><strong>Nenhum gasto lançado</strong><span>Registre óleo, mecânica, pneus e outros.</span></div>}
            {maintenance.map((x) => (
              <div className="list-card" key={x.id}><div><strong>{x.type} • {x.vehicleName || "Veículo"}</strong><span>{x.date ? new Date(x.date + "T12:00:00").toLocaleDateString("pt-BR") : "Sem data"} • {x.description}</span></div><div className="list-value"><strong>{money(x.value)}</strong><button className="danger-lite" onClick={() => delM(x)}>Excluir</button></div></div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function FinanceiroTab({ finance, financeMonth, setFinanceMonth, settings }) {
  const d = finance;
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Financeiro</h1><p>Controle mensal apenas das remoções realizadas e dos custos efetivos.</p></div>
        <div className="finance-head-actions"><label className="month-filter">Mês de referência<input type="month" value={financeMonth} onChange={(e) => setFinanceMonth(e.target.value)} data-testid="finance-month" /></label>{d && <button className="pdf-btn" onClick={() => generateFinancePDF(d, settings)} data-testid="finance-pdf">Fechamento mensal em PDF</button>}</div>
      </div>
      {!d ? <div className="empty-state"><strong>Carregando…</strong></div> : (
        <>
          <div className="finance-summary">
            <article className="report-card"><span>Faturamento</span><strong data-testid="fin-revenue">{money(d.revenue)}</strong></article>
            <article className="report-card"><span>Custos totais</span><strong>{money(d.cost)}</strong></article>
            <article className="report-card positive"><span>Lucro estimado</span><strong>{money(d.profit)}</strong></article>
            <article className="report-card"><span>Margem do mês</span><strong>{Number(d.margin || 0).toFixed(1)}%</strong></article>
          </div>
          <div className="finance-grid">
            <article className="card"><h2>Composição dos custos</h2>
              <div className="result-line"><span>Profissionais</span><strong>{money(d.professionals)}</strong></div>
              <div className="result-line"><span>Combustível</span><strong>{money(d.fuel)}</strong></div>
              <div className="result-line"><span>Pedágios</span><strong>{money(d.tolls)}</strong></div>
              <div className="result-line"><span>Valor médio cobrado por km</span><strong>{money(d.pricePerKm)}</strong></div>
              <div className="result-line"><span>Alimentação / hospedagem / outros</span><strong>{money(d.misc)}</strong></div>
              <div className="result-line"><span>Manutenção da frota</span><strong>{money(d.maintenance)}</strong></div>
            </article>
            <article className="card"><h2>Indicadores do mês</h2>
              <div className="kpi-list"><div><span>Remoções realizadas</span><strong data-testid="fin-count">{d.count}</strong></div><div><span>Km rodados</span><strong>{Number(d.km || 0).toFixed(1)} km</strong></div><div><span>Custo médio por km</span><strong>{money(d.costPerKm)}</strong></div><div><span>Ticket médio</span><strong>{money(d.ticket)}</strong></div></div>
            </article>
          </div>
          <article className="card finance-table-card"><h2>Movimentação do mês</h2>
            <div className="module-list compact">
              {(!d.movements || d.movements.length === 0) && <div className="empty-state"><strong>Sem movimentações neste mês</strong><span>Marque uma remoção como realizada e lance manutenções.</span></div>}
              {(d.movements || []).map((r, i) => (
                <div className="list-card" key={`${r.type}-${r.date}-${i}`}><div><strong>{r.type}</strong><span>{r.desc}</span><small>{r.date ? new Date(String(r.date).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-BR") : ""}</small></div><div className="list-value"><strong className={r.kind === "in" ? "income" : "expense"}>{r.kind === "in" ? "+ " : "- "}{money(r.value)}</strong></div></div>
              ))}
            </div>
          </article>
        </>
      )}
    </section>
  );
}

function UsuariosTab({ users, reload, currentUserId }) {
  const empty = { id: null, name: "", email: "", phone: "", role: "operacional", password: "", status: "ativo" };
  const [form, setForm] = useState(empty);
  const sf = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Nome e e-mail são obrigatórios.");
    const { id, ...body } = form;
    try {
      if (id) await api.patch(`/users/${id}`, body); else await api.post("/users", body);
      toast.success(id ? "Usuário atualizado." : "Usuário criado."); setForm(empty); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (u) => { if (!window.confirm("Excluir usuário?")) return; try { await api.delete(`/users/${u.id}`); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Usuários</h1><p>Gerencie a equipe da empresa e seus perfis de acesso.</p></div></div>
      <div className="module-grid two-col">
        <article className="card"><h2>{form.id ? "Editar usuário" : "Novo usuário"}</h2>
          <div className="form-grid">
            <label>Nome<input value={form.name} onChange={sf("name")} data-testid="user-name" /></label>
            <label>E-mail<input type="email" value={form.email} onChange={sf("email")} disabled={!!form.id} data-testid="user-email" /></label>
            <label>Telefone<input value={form.phone} onChange={sf("phone")} /></label>
            <label>Perfil<select value={form.role} onChange={sf("role")} data-testid="user-role"><option value="admin">Administrador</option><option value="operacional">Operacional</option><option value="financeiro">Financeiro</option></select></label>
            <label>{form.id ? "Nova senha (opcional)" : "Senha"}<input type="password" value={form.password} onChange={sf("password")} data-testid="user-password" placeholder={form.id ? "Deixe vazio para manter" : "Senha inicial"} /></label>
            <label>Status<select value={form.status} onChange={sf("status")}><option value="ativo">Ativo</option><option value="inativo">Inativo</option></select></label>
          </div>
          <div className="inline-actions"><button className="primary" onClick={save} data-testid="user-save">{form.id ? "Atualizar" : "Criar usuário"}</button>{form.id && <button className="secondary" onClick={() => setForm(empty)}>Cancelar</button>}</div>
        </article>
        <article className="card"><h2>Equipe</h2>
          <div className="module-list compact">
            {users.map((u) => (
              <div className="list-card" key={u.id} data-testid={`user-${u.id}`}><div><strong>{u.name}</strong><span>{u.email} • <span className="role-badge">{u.role}</span></span><small>Status: {u.status}{u.lastLoginAt ? ` • último acesso ${new Date(u.lastLoginAt).toLocaleDateString("pt-BR")}` : ""}</small></div><div className="list-value"><button onClick={() => setForm({ id: u.id, name: u.name, email: u.email, phone: u.phone || "", role: u.role, password: "", status: u.status })}>Editar</button>{u.id !== currentUserId && <button className="danger-lite" onClick={() => del(u)}>Excluir</button>}</div></div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function EmpresaTab({ settings, reload }) {
  const [s, setS] = useState(settings);
  useEffect(() => { setS(settings); }, [settings]);
  const sf = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const onLogo = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setS((p) => ({ ...p, logo: r.result })); r.readAsDataURL(file); };
  const save = async () => {
    try { await api.put("/company/settings", { name: s.name, phone: s.phone, email: s.email, doc: s.doc, footer: s.footer, logo: s.logo || "" }); toast.success("Identidade salva."); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <section className="panel active">
      <div className="module-head"><div><h1>Minha Empresa</h1><p>Personalize os documentos e PDFs com a identidade da empresa parceira. A marca RotaSaúde permanece sempre.</p></div></div>
      <div className="module-grid two-col company-grid">
        <article className="card"><h2>Identidade da empresa parceira</h2>
          <div className="company-logo-box">{s.logo ? <img src={s.logo} alt="Logo empresa parceira" /> : <span>Nenhum logo de empresa parceira cadastrado</span>}</div>
          <label className="full-label">Logo da empresa parceira<input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} data-testid="company-logo" /></label>
          <p className="hint">O logo aparece ao lado da marca RotaSaúde no topo e nos PDFs. A marca RotaSaúde nunca será substituída.</p>
          {s.logo && <button className="secondary" onClick={() => setS({ ...s, logo: "" })}>Remover logo da empresa parceira</button>}
        </article>
        <article className="card"><h2>Dados para o documento</h2>
          <div className="form-grid">
            <label>Nome da empresa<input value={s.name || ""} onChange={sf("name")} data-testid="company-name" placeholder="Nome da empresa" /></label>
            <label>Telefone / WhatsApp<input value={s.phone || ""} onChange={sf("phone")} placeholder="(11) 99999-9999" /></label>
            <label>E-mail<input type="email" value={s.email || ""} onChange={sf("email")} placeholder="contato@empresa.com.br" /></label>
            <label>CNPJ / identificação<input value={s.doc || ""} onChange={sf("doc")} placeholder="Opcional" /></label>
          </div>
          <label className="full-label">Rodapé do orçamento<input value={s.footer || ""} onChange={sf("footer")} placeholder="Ex.: Transporte e remoção de pacientes" /></label>
          <button className="primary" onClick={save} data-testid="company-save">Salvar identidade</button>
        </article>
      </div>
    </section>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await api.post("/auth/change-password", { currentPassword: cur, newPassword: nw }); toast.success("Senha alterada."); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Modal title="Alterar senha" onClose={onClose} size={440}
      footer={<><button className="secondary" onClick={onClose} style={{ border: 0, borderRadius: 8, padding: "10px 16px", fontWeight: 800, cursor: "pointer" }}>Cancelar</button><button className="primary" onClick={save} disabled={loading} style={{ border: 0, borderRadius: 8, padding: "10px 16px", fontWeight: 800, cursor: "pointer" }} data-testid="pw-save">Salvar</button></>}>
      <label className="auth-field">Senha atual<input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-current" /></label>
      <label className="auth-field">Nova senha<input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="pw-new" /></label>
    </Modal>
  );
}
